import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import PauseIcon from '@mui/icons-material/Pause';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { statusColors, calendarColors, neutralGrey } from '@/styles/theme';
import { useIsMobile } from '@/hooks/useIsMobile';
import { approvalWaitLabel, showsApprovalWaitMarker } from '@/utils/workOrderStatusChips';
import type { Booking } from '@/types';

const STATUS_ICONS: Record<string, React.ElementType> = {
  pending: ScheduleIcon,
  confirmed: ThumbUpAltIcon,
  vehicle_arrived: DirectionsCarIcon,
  in_progress: BuildIcon,
  paused: PauseIcon,
  waiting_parts: LocalShippingIcon,
  waiting_approval: PendingActionsIcon,
  work_completed: TaskAltIcon,
  ready_to_invoice: ReceiptLongIcon,
  on_consolidated_invoice: ReceiptLongIcon,
  invoiced: ReceiptLongIcon,
  completed: CheckCircleIcon,
  cancelled: CancelIcon,
};

interface BookingItemProps {
  booking: Booking;
  onClick?: () => void;
}

export function BookingItem({ booking, onClick }: BookingItemProps) {
  const { t } = useTranslation('calendar');
  const theme = useTheme();
  const backgroundColor = statusColors[booking.status] ?? neutralGrey;
  const textColor = theme.palette.getContrastText(backgroundColor);
  const isMobile = useIsMobile();
  const StatusIcon = STATUS_ICONS[booking.status] ?? ScheduleIcon;
  // Derived approval wait (#3027): a small badge; the block keeps its status colour.
  const approvalWaitText = showsApprovalWaitMarker(booking)
    ? approvalWaitLabel(t('statusLabels.waiting_approval'), booking.openApprovalCount)
    : null;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {booking.customerName}
          </Typography>
          <Typography variant="caption" display="block">
            {booking.vehicleRegNumber} - {booking.vehicleMakeModel}
          </Typography>
          <Typography variant="caption" display="block">
            {booking.services.map((s) => s.name).join(', ')}
          </Typography>
          {approvalWaitText && (
            <Typography variant="caption" display="block">
              {approvalWaitText}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
      enterTouchDelay={isMobile ? 500 : 100}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        aria-label={`${booking.vehicleRegNumber} — ${booking.customerName}`}
        sx={{
          backgroundColor,
          color: textColor,
          border: `1px solid ${calendarColors.itemBorder}`,
          borderRadius: 1,
          px: 1,
          py: isMobile ? 0.75 : 0.5,
          height: '100%',
          minHeight: isMobile ? 44 : 'auto',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          transition: 'transform 0.1s, box-shadow 0.1s',
          '&:hover, &:focus-visible': {
            transform: 'scale(1.02)',
            boxShadow: 2,
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          sx={{
            fontSize: isMobile ? '0.8rem' : '0.75rem',
            lineHeight: 1.2,
            textTransform: 'uppercase',
            color: 'inherit',
          }}
        >
          {booking.vehicleRegNumber}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'inherit' }}>
          <StatusIcon sx={{ fontSize: isMobile ? 14 : 12, color: 'inherit' }} />
          <Typography
            variant="caption"
            fontWeight={600}
            noWrap
            sx={{ lineHeight: 1.2, fontSize: isMobile ? '0.75rem' : '0.7rem', color: 'inherit' }}
          >
            {t(`statusLabels.${booking.status}` as const)}
          </Typography>
          {approvalWaitText && (
            <PendingActionsIcon
              titleAccess={approvalWaitText}
              sx={{ fontSize: isMobile ? 14 : 12, color: 'inherit', ml: 'auto', flexShrink: 0 }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}
